import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Bot, ChevronDown, Database, Play, ShieldCheck, Sparkles, Target, Workflow, X, Youtube } from 'lucide-react';
import hcmvpThumbnail from '../assets/hcmvp_thumbnail.png';
import { FAQS, PAGES, usePageMeta } from '../seo/seo';

const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@CognitAIX-WebAIExcel';

type DemoVideo = {
  youtubeId: string;
  title: string;
  description: string;
  version?: string;
  latest?: boolean;
  // Optional local image; falls back to YouTube's own thumbnail.
  thumbnail?: string;
};

type DemoGroup = {
  industry: string;
  videos: DemoVideo[];
};

// To add or swap a video, edit this list only.
const DEMO_GROUPS: DemoGroup[] = [
  {
    industry: 'Finance',
    videos: [
      {
        youtubeId: '8eil0ujaYig',
        title: 'WebAIExcel for Finance',
        description: 'The latest walkthrough, including the features added since version 1.',
        version: 'Version 2',
        latest: true,
      },
      {
        youtubeId: 'VY0b1c1wKf4',
        title: 'WebAIExcel for Finance',
        description: 'The original WebAIExcel MVP demo.',
        version: 'Version 1',
      },
    ],
  },
  {
    industry: 'Healthcare',
    videos: [
      {
        youtubeId: 'u5Yrz5Ph3yU',
        title: 'WebAIExcel for Healthcare',
        description: 'The WebAIExcel MVP demo for healthcare workflows.',
        thumbnail: hcmvpThumbnail,
      },
    ],
  },
];

const REASONS = [
  {
    icon: <Sparkles />,
    title: 'Copilot Exists. So Why WebAIExcel?',
    text: 'Copilot is great—but it’s not built for your unique data and workflows. We create AI-powered automation tailored to your exact needs.',
  },
  {
    icon: <Target />,
    title: 'AI is Powerful, But Not Always the Answer',
    text: 'We don’t just add AI for the sake of it. Sometimes, smart automation is the real solution. We focus on what actually removes bottlenecks.',
  },
  {
    icon: <Workflow />,
    title: 'Your Workflow, Your Rules',
    text: 'Forget one-size-fits-all. WebAIExcel integrates seamlessly into your existing processes, so you don’t have to change how you work.',
  },
  {
    icon: <ShieldCheck />,
    title: 'No Risk, No Hype',
    text: 'We start by understanding your needs, prototyping a solution, and refining it with you—before any commitment.',
  },
];

const WebAIExcelPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  usePageMeta(PAGES.webaiexcel);
  const [activeVideo, setActiveVideo] = useState<DemoVideo | null>(null);

  // Lets other pages link straight to a section, e.g. /webaiexcel#demos.
  useEffect(() => {
    if (location.hash) {
      document.getElementById(location.hash.slice(1))?.scrollIntoView();
    }
  }, [location.hash]);

  const handleContactButtonClick = () => {
    navigate('/contact');
  };

  const handleWatchDemosClick = () => {
    document.getElementById('demos')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCloseVideo = useCallback(() => setActiveVideo(null), []);

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Hero */}
      <section className="bg-gradient-to-r from-brandPurple to-brandBlue text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-white/80 mb-4">WebAIExcel</p>
          <h1 className="text-4xl md:text-5xl font-bold max-w-5xl mx-auto leading-tight">
            AI & Web Automation, Built for Your Excel
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            A customizable solution that brings AI and automation into the Excel workflows you already use.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button
              type="button"
              onClick={handleWatchDemosClick}
              className="flex items-center gap-2 px-6 py-3 bg-white text-brandPurple font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300"
            >
              <Play className="w-5 h-5" /> Watch the demos
            </button>
            <button
              type="button"
              onClick={handleContactButtonClick}
              className="flex items-center gap-2 px-6 py-3 border border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300"
            >
              Get in touch <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Problem statement */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Excel isn’t broken. But is it keeping up?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
            Excel is still the backbone of financial analysis & reporting, but off-the-shelf AI tools like Copilot
            don’t understand your data, your processes, or your workflow challenges. WebAIExcel isn’t just another
            AI add-in—it’s a <span className="font-semibold text-gray-900 dark:text-white">customizable solution designed to fit exactly what you need.</span>
          </p>
        </div>
      </section>

      {/* Why WebAIExcel */}
      <section className="py-16 md:py-20 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#3399FF] dark:text-white mb-12">
            Why WebAIExcel?
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {REASONS.map((reason) => (
              <div
                key={reason.title}
                className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-[#36AC98] transition-colors duration-300"
              >
                <div className="inline-flex p-3 rounded-lg bg-[#3399FF]/10 text-[#3399FF] dark:text-[#36AC98] mb-4">
                  {React.cloneElement(reason.icon, { size: 24 })}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{reason.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{reason.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo videos */}
      <section id="demos" className="py-16 md:py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3399FF] dark:text-white">See It in Action</h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              Watch WebAIExcel working inside Excel for real finance and healthcare workflows.
            </p>
          </div>

          {DEMO_GROUPS.map((group) => (
            <div key={group.industry} className="mb-12 last:mb-0">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">{group.industry}</h3>
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {group.videos.map((video) => (
                  <VideoCard
                    key={video.youtubeId}
                    video={video}
                    industry={group.industry}
                    onPlay={() => setActiveVideo(video)}
                  />
                ))}
              </div>
            </div>
          ))}

          <div className="mt-12 text-center">
            <a
              href={YOUTUBE_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-medium text-gray-700 dark:text-gray-200 hover:text-brandPurple dark:hover:text-brandBlue transition-colors duration-300"
            >
              <Youtube className="w-5 h-5" /> More videos on our YouTube channel
            </a>
          </div>
        </div>
      </section>

      {/* What it solves */}
      <section className="py-16 md:py-20 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#3399FF] dark:text-white mb-12">
            What WebAIExcel Solves
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <WAECard
              icon={<Database />}
              title="Handling Large Datasets Efficiently"
              issue="We know Excel, struggles with large datasets, causing slow performance, frequent crashes, and inefficient financial modeling."
              features={[
                'Server-Side Data Processing – Offloads large datasets to a secure database for faster computations.',
                'Preprocessed Data – Optimizes and loads only relevant data into Excel, reducing memory usage.',
                'Real-Time Performance – No more waiting for heavy calculations or broken pivot tables.',
                'Seamless Integration – Works as an Excel task-pane add-in, allowing smooth user adoption.',
              ]}
            />
            <WAECard
              icon={<Bot />}
              title="AI-Driven Automation & Real-Time Data Insights"
              issue="Manual processes for data refresh, financial reporting, and formula calculations lead to errors & inefficiencies."
              features={[
                'In-Memory Data Processing – Enables real-time market data updates without refreshing the entire sheet.',
                'AI-Powered Custom Functions – Converts complex formulas into smart reusable functions, reducing human errors.',
                'Automated Report Generation – Schedule and export your reports directly as PDF or Excel.',
                'Integration with Python & R – Leverage advanced statistical & risk modeling inside Excel.',
              ]}
            />
            <WAECard
              icon={<ShieldCheck />}
              title="Secure, Collaborative & Audit-Ready Excel Workflows"
              issue="No version control, role-based access, or secure collaboration, making enterprise data prone to tampering & compliance risks."
              features={[
                'Role-Based Access (RBAC) – Prevents unauthorized modifications with controlled data permissions.',
                'Versioning & Audit Logs – Every change is tracked & recoverable, ensuring compliance (Basel III, IFRS, SOX, FDA, EMA, MHRA).',
                'Excel Image Mapping – Stores secure snapshots in a database, allowing multi-user collaboration without conflicts.',
                'Enterprise-Grade Security – Eliminates file-based vulnerabilities, ensuring data integrity.',
              ]}
            />
          </div>
        </div>
      </section>

      {/* FAQ: native <details> keeps every answer in the HTML for search and AI crawlers */}
      <section id="faq" className="py-16 md:py-20 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#3399FF] dark:text-white mb-12">
            Frequently Asked Questions
          </h2>
          <div className="divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
            {FAQS.map((faq) => (
              <details key={faq.question} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none text-lg font-semibold text-gray-900 dark:text-white [&::-webkit-details-marker]:hidden">
                  <h3>{faq.question}</h3>
                  <ChevronDown className="w-5 h-5 flex-shrink-0 text-gray-500 transition-transform duration-300 group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Call to action */}
      <section className="py-16 bg-[#3399ff]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Excel isn’t stuck in the past—why should your workflows be?
          </h2>
          <p className="text-lg text-white mb-8">Curious to see what’s possible? Let’s talk.</p>
          <button
            type="button"
            onClick={handleContactButtonClick}
            className="px-8 py-3 bg-white text-[#3399FF] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300"
          >
            Get in Touch
          </button>
        </div>
      </section>

      {activeVideo && <VideoModal video={activeVideo} onClose={handleCloseVideo} />}
    </div>
  );
};

interface VideoCardProps {
  video: DemoVideo;
  industry: string;
  onPlay: () => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ video, industry, onPlay }) => {
  const thumbnail = video.thumbnail ?? `https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`;

  return (
    <div className="flex flex-col bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow duration-300">
      <button
        type="button"
        onClick={onPlay}
        className="group relative aspect-video w-full overflow-hidden bg-gray-900"
        aria-label={`Play ${video.title}${video.version ? ` (${video.version})` : ''}`}
      >
        <img
          src={thumbnail}
          alt=""
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors duration-300">
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-white/90 text-brandPurple shadow-lg transition-transform duration-300 group-hover:scale-110">
            <Play className="w-7 h-7 ml-1" fill="currentColor" />
          </span>
        </div>
      </button>
      <div className="p-5 flex-1">
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#3399FF]/10 text-[#3399FF]">{industry}</span>
          {video.version && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              {video.version}
            </span>
          )}
          {video.latest && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#36AC98]/15 text-[#36AC98]">Latest</span>
          )}
        </div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{video.title}</h4>
        <p className="text-gray-600 dark:text-gray-300">{video.description}</p>
      </div>
    </div>
  );
};

interface VideoModalProps {
  video: DemoVideo;
  onClose: () => void;
}

const VideoModal: React.FC<VideoModalProps> = ({ video, onClose }) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const label = `${video.title}${video.version ? ` (${video.version})` : ''}`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
    >
      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors"
          aria-label="Close video"
        >
          <X className="w-7 h-7" />
        </button>
        <div className="aspect-video w-full overflow-hidden rounded-xl bg-black shadow-2xl">
          <iframe
            className="w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0`}
            title={label}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

interface WAECardProps {
  icon: React.ReactElement<{ size?: number }>;
  title: string;
  issue: string;
  features: string[];
}

const WAECard: React.FC<WAECardProps> = ({ icon, title, issue, features }) => {
  return (
    <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-[#36AC98] transition-colors duration-300">
      <div className="text-[#3399FF] dark:text-[#36AC98] mb-4">
        {React.cloneElement(icon, { size: 32 })}
      </div>
      <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300 mb-4">{issue}</p>
      <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">WebAIExcel Solution:</h4>
      <ul className="space-y-2">
        {features.map((feature) => (
          <li key={feature} className="flex items-start text-gray-600 dark:text-gray-300">
            <div className="w-1.5 h-1.5 rounded-full bg-[#36AC98] mr-2 mt-2.5 flex-shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default WebAIExcelPage;
